/**
 * Payout block for the offer edit dialog — amount + currency + Auto + Upsell.
 * Reads/writes form.payout, leaving other Binom fields untouched.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Input } from '@/components/ui/form/input/Input';
import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import type { BinomOfferDetail } from '@/hooks/binom';

import OfferEditField from '../OfferEditField';

export interface BinomOfferPayoutFieldsProps {
  form: BinomOfferDetail;
  onChange: (next: BinomOfferDetail) => void;
}

interface PayoutShape {
  amount?: number;
  currency?: string;
  isAuto?: boolean;
  upsell?: boolean;
}

function getPayout(form: BinomOfferDetail): PayoutShape {
  return (form.payout as PayoutShape | undefined) ?? {};
}

const BinomOfferPayoutFields: FC<BinomOfferPayoutFieldsProps> = ({
  form,
  onChange,
}) => {
  const payout = getPayout(form);
  const set = (patch: Partial<PayoutShape>) =>
    onChange({ ...form, payout: { ...payout, ...patch } } as BinomOfferDetail);

  return (
    <OfferEditField label="Payout">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="number"
          step="0.01"
          min={0}
          value={payout.amount ?? 0}
          onChange={(e) => set({ amount: Number(e.target.value) || 0 })}
          className="h-7 w-24 text-right text-xs"
          aria-label="Payout amount"
        />
        <Input
          value={payout.currency ?? ''}
          onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
          className="h-7 w-16 text-xs"
          placeholder="USD"
          aria-label="Payout currency"
        />
        <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
          <Checkbox
            checked={Boolean(payout.isAuto)}
            onCheckedChange={(v) => set({ isAuto: Boolean(v) })}
            aria-label="Auto payout"
          />
          <span>Auto</span>
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
          <Checkbox
            checked={Boolean(payout.upsell)}
            onCheckedChange={(v) => set({ upsell: Boolean(v) })}
            aria-label="Upsell"
          />
          <span>Upsell</span>
        </label>
      </div>
    </OfferEditField>
  );
};

export default BinomOfferPayoutFields;
